import { faSliders } from '@fortawesome/free-solid-svg-icons/faSliders';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useGetInternalWidgetConfig from '@hooks/api/queries/widgets/useGetInternalWidgetConfig';
import useSubscription from '@hooks/useSubscription';
import { Box, Stack, Tooltip } from '@mui/material';
import { isActionRole } from '@src/services/auth';
import { UserRole } from '@src/services/auth/roles';
import storeConnector from '@src/store/storeConnector';
import { DataPoint } from '@src/types/DataPoint';
import { SiteMeta } from '@src/types/SiteMeta';
import { WidgetTypePointList } from '@src/types/Widget';
import isCommandsEnabled from '@src/utils/isCommandsEnabled';
import { memo } from 'react';
import ActiveModesList from '../Home/Config/Modes/ActiveModesList';
import NotesButton from './Notes/NotesButton';
import SimplePointDataTable from './SimplePointDataTable';
import { sysList } from './sxStyles/sysList';
import FormattedAlias from './Translations/FormattedAlias';

interface SystemProps {
  siteMeta: SiteMeta;
  role: UserRole;
  isControlsInterLocked: boolean;
  actions: {
    showCommandsPopup: (
      show: boolean,
      options: { popupType: string; sn: string },
    ) => void;
  };
  isChild?: boolean;
  isHome?: boolean;
  sectionId?: string;
}

const SystemPointList = memo(function SystemPointList({
  dataPoints,
}: {
  dataPoints: DataPoint[];
}) {
  const dpWtihValues = useSubscription(dataPoints);
  return (
    <Box mt={2}>
      <SimplePointDataTable data={dpWtihValues} />
    </Box>
  );
});

function System({
  siteMeta,
  role,
  isControlsInterLocked,
  actions,
  isChild,
  isHome,
  sectionId,
}: SystemProps) {
  const { controlsLockConfig, System_Modes_Application_Disable } = siteMeta.ui;
  const tooltipTitle = `Controls disabled. ${controlsLockConfig?.pointName} is above the threshold of ${controlsLockConfig?.thresholdMax}.`;
  const {
    data: widgetConfig,
    isLoading,
    isError,
  } = useGetInternalWidgetConfig<WidgetTypePointList>(
    sectionId ?? 'systemPointsList',
    {},
    {
      queryKeys: ['systemPointsList'],
    },
  );

  const handleCommandClick = () => {
    if (!isControlsInterLocked) {
      actions.showCommandsPopup(true, {
        popupType: 'site',
        sn: siteMeta.siteId,
      });
    }
  };

  return (
    <Box {...(!isChild && { isLoading, isError })}>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Box>
          <h4>
            <FormattedAlias id='System' />
          </h4>
        </Box>
        {!isHome && (
          <Stack direction='row' spacing={1} alignItems='center'>
            {isActionRole(role) && siteMeta.ui.Notes_Enable ? (
              <NotesButton
                className='cell-with-cursor-pointer'
                size='large'
                deviceId={siteMeta.siteId}
                deviceName={`${siteMeta.SiteName} Site Controller`}
              />
            ) : null}
            {isActionRole(role) && isCommandsEnabled() ? (
              <Tooltip
                title={isControlsInterLocked ? tooltipTitle : ''}
                placement='top'
                disableHoverListener={!isControlsInterLocked}
              >
                <FontAwesomeIcon
                  className={
                    isControlsInterLocked
                      ? 'disabled-icon-btn'
                      : 'cell-with-cursor-pointer rounded-border'
                  }
                  onClick={handleCommandClick}
                  icon={faSliders}
                  size={'2xl'}
                />
              </Tooltip>
            ) : null}
          </Stack>
        )}
      </Stack>
      {widgetConfig?.dataPoints?.[0] && (
        <SystemPointList dataPoints={widgetConfig.dataPoints[0]} />
      )}
      {!System_Modes_Application_Disable && (
        <Stack
          direction='row'
          justifyContent={'space-between'}
          sx={sysList.pointGroupItem}
        >
          <span style={sysList.pointItemName}>
            <FormattedAlias id='Application' />
          </span>
          <ActiveModesList />
        </Stack>
      )}
    </Box>
  );
}

export default storeConnector(System, {
  user: ['role'],
  config: ['siteMeta'],
  mqtt: ['topicDict'],
  service: ['isControlsInterLocked'],
});
